import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Switch from '@radix-ui/react-switch';
import { Theme, Button, Text, Flex } from '@radix-ui/themes';
import MarkdownEditor from '../../shared/MarkdownEditor/MarkdownEditor';
import styles from './Changelog.module.css';

const VALID_PAGES = [
  'campaigns',
  'plan',
  'tiles',
  'users',
  'reports',
  'broadcasts',
  'vault',
  'hr_assistants',
  'goals',
];

const PAGE_LABELS = {
  campaigns: 'Campaigns',
  plan: 'Calendar',
  tiles: 'Highlights',
  users: 'Users',
  reports: 'Reports',
  broadcasts: 'Broadcasts',
  vault: 'Vault',
  hr_assistants: 'HR Assistants',
  goals: 'Goals',
};

const formatDateForInput = dateString => {
  if (!dateString) return new Date().toISOString().split('T')[0];
  return dateString.split('T')[0];
};

const ReleaseFormDialog = ({
  open,
  onOpenChange,
  release,
  onSubmit,
  onDelete,
  isSubmitting,
}) => {
  const isEdit = Boolean(release);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaLabel, setCtaLabel] = useState("See what's new");
  const [releasedAt, setReleasedAt] = useState(formatDateForInput());
  const [status, setStatus] = useState('draft');
  const [affectedPages, setAffectedPages] = useState([]);

  useEffect(() => {
    if (open) {
      if (release) {
        setTitle(release.title || '');
        setBody(release.body || '');
        setCtaUrl(release.cta_url || '');
        setCtaLabel(release.cta_label || "See what's new");
        setReleasedAt(formatDateForInput(release.released_at));
        setStatus(release.status || 'draft');
        setAffectedPages(release.affected_pages || []);
      } else {
        setTitle('');
        setBody('');
        setCtaUrl('');
        setCtaLabel("See what's new");
        setReleasedAt(formatDateForInput());
        setStatus('draft');
        setAffectedPages([]);
      }
    }
  }, [release, open]);

  const handlePageToggle = page => {
    setAffectedPages(prev =>
      prev.includes(page) ? prev.filter(p => p !== page) : [...prev, page],
    );
  };

  const handleSubmit = e => {
    e.preventDefault();
    const data = {
      title,
      body,
      cta_url: ctaUrl,
      cta_label: ctaLabel,
      released_at: releasedAt,
      status,
      affected_pages: affectedPages,
    };
    if (isEdit) {
      data.id = release.id;
    }
    onSubmit(data);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.dialogOverlay} />
        <Theme>
          <Dialog.Content className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <Dialog.Title asChild>
                <Text as="h2" size="4" weight="bold">
                  {isEdit ? 'Edit Entry' : 'Add Entry'}
                </Text>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className={styles.closeButton}>×</button>
              </Dialog.Close>
            </div>

            <form onSubmit={handleSubmit}>
              <div className={styles.dialogBody}>
                <Flex direction="column" gap="4">
                  <div className={styles.formField}>
                    <Text as="label" size="2" weight="medium">
                      Title
                    </Text>
                    <input
                      type="text"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      className={styles.formInput}
                      placeholder="What changed?"
                      required
                    />
                  </div>

                  <div className={styles.formField}>
                    <Text as="label" size="2" weight="medium">
                      Details (Markdown)
                    </Text>
                    <MarkdownEditor
                      value={body}
                      onChange={setBody}
                      placeholder="Describe the changes..."
                    />
                  </div>

                  <div className={styles.formField} style={{ margin: 0 }}>
                    <Text as="label" size="2" weight="medium">
                      Button URL
                    </Text>
                    <input
                      type="url"
                      value={ctaUrl}
                      onChange={e => setCtaUrl(e.target.value)}
                      className={styles.formInput}
                      placeholder="https://example.com/feature"
                    />
                  </div>

                  <div className={styles.formField} style={{ margin: 0 }}>
                    <Text as="label" size="2" weight="medium">
                      Button Label
                    </Text>
                    <input
                      type="text"
                      value={ctaLabel}
                      onChange={e => setCtaLabel(e.target.value)}
                      className={styles.formInput}
                      placeholder="See what's new"
                    />
                  </div>

                  <div className={styles.formField}>
                    <Text as="label" size="2" weight="medium">
                      Date
                    </Text>
                    <input
                      type="date"
                      value={releasedAt}
                      onChange={e => setReleasedAt(e.target.value)}
                      className={styles.formInput}
                    />
                  </div>

                  <div className={styles.formField}>
                    <Text as="label" size="2" weight="medium">
                      Affected Pages
                    </Text>
                    <div className={styles.pagesGrid}>
                      {VALID_PAGES.map(page => (
                        <label key={page} className={styles.pageCheckbox}>
                          <input
                            type="checkbox"
                            checked={affectedPages.includes(page)}
                            onChange={() => handlePageToggle(page)}
                          />
                          <Text size="2">{PAGE_LABELS[page]}</Text>
                        </label>
                      ))}
                    </div>
                  </div>

                  <label className={styles.toggleField}>
                    <Text size="2" weight="medium">
                      Published
                    </Text>
                    <Switch.Root
                      className={styles.switchRoot}
                      checked={status === 'published'}
                      onCheckedChange={checked =>
                        setStatus(checked ? 'published' : 'draft')
                      }
                    >
                      <Switch.Thumb className={styles.switchThumb} />
                    </Switch.Root>
                  </label>
                </Flex>
              </div>

              <div className={styles.dialogFooter}>
                {isEdit && onDelete && (
                  <Button
                    type="button"
                    variant="soft"
                    color="red"
                    onClick={() => onDelete(release)}
                    className={styles.deleteButton}
                  >
                    Delete
                  </Button>
                )}
                <div className={styles.footerActions}>
                  <Dialog.Close asChild>
                    <Button variant="soft" color="gray" type="button">
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </form>
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ReleaseFormDialog;
